/** Payments page with payment history and management. */
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  Grid,
} from "@mui/material";
import {
  Refresh,
  Add,
  CreditCard,
  AccountBalance,
  Check,
  Warning,
} from "@mui/icons-material";
import { format } from "date-fns";
import { paymentsApi, invoicesApi } from "../api/client";

interface Payment {
  id: string;
  invoice_id: string;
  amount: number;
  payment_method: string;
  transaction_id: string;
  status: string;
  paid_at: string;
  created_at: string;
}

interface Invoice {
  id: string;
  invoice_number: string;
  customer_id: string;
  total_amount: number;
  currency: string;
  status: string;
  due_date: string;
}

const statusColors: Record<string, "success" | "warning" | "error" | "default"> = {
  completed: "success",
  pending: "warning",
  failed: "error",
  cancelled: "default",
};

const methodLabels: Record<string, string> = {
  stripe_card: "Credit Card (Stripe)",
  wire: "Wire Transfer",
  check: "Check",
  ach: "ACH Transfer",
};

/** Payments page component. */
const Payments: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    invoice_id: "",
    amount: "",
    payment_method: "wire",
    transaction_id: "",
  });

  // Fetch payments and invoices
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all invoices
      const invoicesResponse = await invoicesApi.list({ page_size: 100 });
      setInvoices(invoicesResponse.data);

      // For now, we'll get payments by checking each invoice
      // In production, you'd want a dedicated list endpoint
      const allPayments: Payment[] = [];
      for (const invoice of invoicesResponse.data) {
        try {
          const paymentsResponse = await paymentsApi.listForInvoice(invoice.id);
          allPayments.push(...paymentsResponse.data);
        } catch (e) {
          // Skip invoices with no payments
        }
      }
      setPayments(allPayments);
      setTotalCount(allPayments.length);
    } catch (error) {
      console.error("Failed to fetch payments:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize]);

  const handleCreatePayment = async () => {
    try {
      await paymentsApi.create({
        invoice_id: paymentData.invoice_id,
        amount: parseFloat(paymentData.amount),
        payment_method: paymentData.payment_method,
        transaction_id: paymentData.transaction_id || undefined,
      });
      setCreateDialogOpen(false);
      setPaymentData({
        invoice_id: "",
        amount: "",
        payment_method: "wire",
        transaction_id: "",
      });
      fetchData();
    } catch (error) {
      console.error("Failed to create payment:", error);
      alert("Failed to create payment");
    }
  };

  const getInvoiceById = (invoiceId: string) => {
    return invoices.find((inv) => inv.id === invoiceId);
  };

  const unpaidInvoices = invoices.filter(
    (inv) => inv.status !== "paid" && inv.status !== "cancelled"
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Payments
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Record Payment
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Payments
              </Typography>
              <Typography variant="h5">
                ${payments
                  .filter((p) => p.status === "completed")
                  .reduce((sum, p) => sum + p.amount, 0)
                  .toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Pending Payments
              </Typography>
              <Typography variant="h5">
                ${payments
                  .filter((p) => p.status === "pending")
                  .reduce((sum, p) => sum + p.amount, 0)
                  .toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h5">
                {payments.filter((p) => p.status === "completed").length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Failed
              </Typography>
              <Typography variant="h5">
                {payments.filter((p) => p.status === "failed").length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Payment ID</TableCell>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments
                  .slice((page - 1) * pageSize, page * pageSize)
                  .map((payment) => {
                    const invoice = getInvoiceById(payment.invoice_id);
                    return (
                      <TableRow key={payment.id}>
                        <TableCell>{payment.id.slice(0, 8)}...</TableCell>
                        <TableCell>
                          {invoice?.invoice_number || payment.invoice_id.slice(0, 8)}
                        </TableCell>
                        <TableCell>${payment.amount.toFixed(2)}</TableCell>
                        <TableCell>{methodLabels[payment.payment_method] || payment.payment_method}</TableCell>
                        <TableCell>
                          <Chip
                            label={payment.status}
                            color={statusColors[payment.status] || "default"}
                            size="small"
                            icon={
                              payment.status === "completed" ? (
                                <Check fontSize="small" />
                              ) : payment.status === "failed" ? (
                                <Warning fontSize="small" />
                              ) : undefined
                            }
                          />
                        </TableCell>
                        <TableCell>{payment.transaction_id || "-"}</TableCell>
                        <TableCell>
                          {payment.paid_at
                            ? format(new Date(payment.paid_at), "MMM dd, yyyy")
                            : format(new Date(payment.created_at), "MMM dd, yyyy")}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {payments.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No payments found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={pageSize}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(1);
            }}
          />
        </CardContent>
      </Card>

      {/* Create Payment Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Invoice</InputLabel>
            <Select
              value={paymentData.invoice_id}
              label="Invoice"
              onChange={(e) => {
                const invoice = unpaidInvoices.find((inv) => inv.id === e.target.value);
                setPaymentData({
                  ...paymentData,
                  invoice_id: e.target.value,
                  amount: invoice ? String(invoice.total_amount) : paymentData.amount,
                });
              }}
            >
              {unpaidInvoices.map((invoice) => (
                <MenuItem key={invoice.id} value={invoice.id}>
                  {invoice.invoice_number} - ${invoice.total_amount.toFixed(2)} ({invoice.status})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="number"
            label="Amount"
            value={paymentData.amount}
            onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Payment Method</InputLabel>
            <Select
              value={paymentData.payment_method}
              label="Payment Method"
              onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
            >
              <MenuItem value="wire">Wire Transfer</MenuItem>
              <MenuItem value="check">Check</MenuItem>
              <MenuItem value="ach">ACH Transfer</MenuItem>
              <MenuItem value="stripe_card">Credit Card (Stripe)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Transaction ID (Optional)"
            value={paymentData.transaction_id}
            onChange={(e) => setPaymentData({ ...paymentData, transaction_id: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreatePayment} variant="contained">Record Payment</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Payments;
